/**
 * Admin → Pending agent PRs (Phase 28.10).
 *
 * Polls the backend for open PRs whose head branch matches agents/*.
 * These are structural agent config changes awaiting human review + merge.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listPendingAgentPRs } from "../services/adminAgents.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

function FormattedDate({ iso }) {
  if (!iso) return null;
  const label = new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return <time dateTime={iso}>{label}</time>;
}

export default function AdminAgentsPending() {
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPrs = useCallback(async () => {
    try {
      const data = await listPendingAgentPRs();
      setPrs(data.prs || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrs();
  }, [fetchPrs]);

  function handleRefresh() {
    setLoading(true);
    fetchPrs();
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Pending Agent PRs</h1>
        <button className="btn btn-secondary" onClick={handleRefresh}>
          Refresh
        </button>
      </div>

      <p className="admin-page__subtext">
        These pull requests contain structural agent configuration changes and
        are waiting for review and merge.{" "}
        <Link to="/admin/agents">← Back to agents</Link>
      </p>

      {error && <ErrorBubble message={error} />}
      {loading ? (
        <LoadingSkeleton variant="line" />
      ) : prs.length === 0 ? (
        <p className="admin-page__empty">No pending agent pull requests.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Branch</th>
              <th>Author</th>
              <th>Opened</th>
              <th>Link</th>
            </tr>
          </thead>
          <tbody>
            {prs.map((pr) => (
              <tr key={pr.number}>
                <td>{pr.number}</td>
                <td>{pr.title}</td>
                <td>
                  <code>{pr.branch}</code>
                </td>
                <td>{pr.author}</td>
                <td>
                  <FormattedDate iso={pr.createdAt} />
                </td>
                <td>
                  <a
                    href={pr.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="admin-action-link"
                  >
                    Review →
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
